import { normalizeDateOnly, isDateMatch, isDateInWeek, isDateWithinWindow, isDateWindowInWeek, getDayAbbreviation } from "../utils/dateUtils";
import { parseColumnValue } from "../utils/dataParsingUtils";
import { isTaskDone } from "../utils/dataParsingUtils";
import { cleanTaskName } from "../utils/textUtils";
import { DONE_LABELS } from "../constants/statusLabels";
import {
  LAST_COMPLETED_DATE_COLUMN_ID,
  METER_READINGS_LAST_COMPLETED_DATE_COLUMN_ID,
  TASKS_DESCRIPTION_COLUMN_ID,
  TIME_TRACKING_COLUMN_ID,
  METER_READINGS_NOTES_COLUMN_ID,
  WEEKLY_CHECKS_BOARD_ID,
  METER_READINGS_BOARD_ID,
  TASKS_BOARD_ID,
  CLEANING_CHECKS_BOARD_ID,
} from "../constants/columnIds";

// Helper function to create a task entry
export function createTaskEntry(item, columnValues, propertyNames, personNames, personText, taskDate, isCompletedEntry = false, propertyItemIds = [], itemConfig = null, providedLastCompletedDate = null, providedLastCompletedDateText = null, config = null, boardId = null, taskType = null, extras = null, statusColumnIdOverride = null) {
  const currentConfig = itemConfig || item._config || config;
  // For Cleaning Checks, use the override status column ID (check-in or check-out)
  const statusColumnId = statusColumnIdOverride || currentConfig.statusColumnId;
  const statusText = parseColumnValue(columnValues, statusColumnId);
  const done = isTaskDone(statusText, DONE_LABELS);

  // Use provided last completed date, or parse it for Weekly Checks or Meter Readings boards
  let lastCompletedDateText = providedLastCompletedDateText;
  let lastCompletedDate = providedLastCompletedDate;
  
  // If not provided and this is a Weekly Checks item, parse it
  if (!lastCompletedDateText && item._boardId === WEEKLY_CHECKS_BOARD_ID) {
    lastCompletedDateText = parseColumnValue(
      columnValues,
      LAST_COMPLETED_DATE_COLUMN_ID
    );
    lastCompletedDate = lastCompletedDateText ? normalizeDateOnly(lastCompletedDateText) : null;
  }
  // If not provided and this is a Meter Readings item, parse it
  else if (!lastCompletedDateText && item._boardId === METER_READINGS_BOARD_ID) {
    lastCompletedDateText = parseColumnValue(
      columnValues,
      METER_READINGS_LAST_COMPLETED_DATE_COLUMN_ID
    );
    lastCompletedDate = lastCompletedDateText ? normalizeDateOnly(lastCompletedDateText) : null;
  }

  // Parse description for TASKS board
  let description = null;
  if (item._boardId === TASKS_BOARD_ID) {
    description = parseColumnValue(columnValues, TASKS_DESCRIPTION_COLUMN_ID);
  }

  // Parse Time Tracking for TASKS board (status/color column)
  let timeTracking = null;
  if (item._boardId === TASKS_BOARD_ID) {
    // For status/color columns, check both text and parsed value
    const timeTrackingCol = columnValues.find((c) => c.id === TIME_TRACKING_COLUMN_ID);
    let timeTrackingText = null;
    
    if (timeTrackingCol) {
      // First try the text property (human-readable label)
      timeTrackingText = timeTrackingCol.text;
      
      // If text is not available, try parsing the value JSON
      if (!timeTrackingText && timeTrackingCol.value) {
        try {
          const parsed = JSON.parse(timeTrackingCol.value);
          // Status columns can have { label: "Yes" } format
          if (parsed.label) {
            timeTrackingText = parsed.label;
          } else if (typeof parsed === 'string') {
            timeTrackingText = parsed;
          }
        } catch (e) {
          // Not JSON, continue with text
        }
      }
    }
    
    // Check if Time Tracking is set to "Yes" (case-insensitive)
    timeTracking = timeTrackingText && timeTrackingText.toLowerCase().trim() === "yes" ? "yes" : "no";
  }

  // Parse Notes for Meter Readings board
  let notes = null;
  if (item._boardId === METER_READINGS_BOARD_ID) {
    notes = parseColumnValue(columnValues, METER_READINGS_NOTES_COLUMN_ID);
  }

  // For Cleaning Checks, use the item name from the Cleaning Checks board
  let taskName;
  if (item._boardId === CLEANING_CHECKS_BOARD_ID) {
    // Use the Cleaning Checks board item name
    const baseName = cleanTaskName(item.name);
    taskName = taskType ? `${baseName} (${taskType})` : baseName;
  } else {
    taskName = cleanTaskName(item.name);
  }

  return {
    id: item.id,
    name: taskName,
    done,
    statusText,
    date: taskDate,
    personNames,
    personText,
    lastCompletedDate,
    lastCompletedDateText,
    isCompletedEntry, // Flag to indicate this is a completion entry
    propertyItemIds, // Store property item IDs for linking reports
    boardId: item._boardId || boardId, // Store board ID for coloring
    boardColor: currentConfig?.color || "#323338", // Store board color
    description, // Store description for TASKS board
    timeTracking, // Store Time Tracking status for TASKS board
    subitems: item.subitems || [], // Store subitems/subtasks
    taskType, // Store task type for Cleaning Checks (check-in or check-out)
    extras, // Store extras for Cleaning Checks
    notes, // Store notes for Meter Readings
  };
}

// Helper function to add task to tasksByProperty
export function addTaskToGroup(taskData, propertyNames, taskDate, viewType, tasksByProperty) {
  propertyNames.forEach((propName) => {
    if (viewType === "weekly") {
      // Weekly view: Group by day first, then property
      const dayKey = taskDate ? getDayAbbreviation(taskDate) : "Other";
      if (!tasksByProperty[dayKey]) {
        tasksByProperty[dayKey] = {};
      }
      if (!tasksByProperty[dayKey][propName]) {
        tasksByProperty[dayKey][propName] = [];
      }
      tasksByProperty[dayKey][propName].push(taskData);
    } else {
      // Daily view: Group by property
      if (!tasksByProperty[propName]) {
        tasksByProperty[propName] = [];
      }
      tasksByProperty[propName].push(taskData);
    }
  });
}

// Helper function to check if task matches person filter
export function matchesPersonFilter(taskPersonNames, selectedPerson, taskItem = null) {
  if (selectedPerson === "all" || !selectedPerson) {
    return true; // Show all tasks when "all" is selected or no filter
  }
  // When filtering by a specific person, only show tasks assigned to that person
  // Tasks with no person assigned should not show
  if (!taskPersonNames || taskPersonNames.length === 0) {
    return false;
  }
  
  // Normalize both sides for comparison (case-insensitive, trimmed)
  // Handle both string and number types for selectedPerson
  const normalizedSelected = String(selectedPerson).trim().toLowerCase();
  
  // Compare with each task person name
  const matches = taskPersonNames.some((name) => {
    if (!name) return false;
    const normalizedName = String(name).trim().toLowerCase();
    return normalizedName === normalizedSelected;
  });
  
  return matches;
}

